import { useEffect, useState, useCallback } from 'react';
/* eslint-disable */

import { Box, Typography, Container, Stack, IconButton, Paper, Avatar, CircularProgress, Tooltip, Breadcrumbs, Link } from '@mui/material';
import {
    ArrowBackIosNewOutlined,
    AccountTreeOutlined,
    ChevronRightOutlined,
    RefreshRounded,
    HomeOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    RestartAltOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { getHierarchy } from 'utils/API/Mis Apis/HrPortelApis';

// ─── Constants & Utils ───────────────────────────────────────────
const ROLE_CONFIG = {
    NH: { color: '#1e40af', label: 'National Head', bg: 'rgba(30, 64, 175, 0.08)' },
    ZH: { color: '#3b82f6', label: 'Zone Head', bg: 'rgba(59, 130, 246, 0.08)' },
    SH: { color: '#14b8a6', label: 'State Head', bg: 'rgba(20, 184, 166, 0.08)' },
    TL: { color: '#8b5cf6', label: 'Team Leader', bg: 'rgba(139, 92, 246, 0.08)' },
    FL: { color: '#f59e0b', label: 'Frontline', bg: 'rgba(245, 158, 11, 0.08)' }
};

const getRoleConfig = (role) => ROLE_CONFIG[role] || { color: '#64748b', label: role || 'Member', bg: 'rgba(100, 116, 139, 0.08)' };

// ─── Styles ──────────────────────────────────────────────────────
const glassStyle = {
    bgcolor: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px'
};

const cardStyles = (color, isExpanded) => ({
    p: 2,
    width: 300,
    borderRadius: '20px',
    boxShadow: isExpanded ? '0 10px 40px rgba(0,0,0,0.12)' : '0 4px 15px rgba(0,0,0,0.05)',
    border: '1px solid',
    borderColor: isExpanded ? `${color}40` : 'divider',
    bgcolor: '#fff',
    position: 'relative',
    zIndex: 2,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 15px 45px rgba(0,0,0,0.15)',
        borderColor: color
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '15%',
        height: '70%',
        width: '4px',
        bgcolor: color,
        borderRadius: '0 4px 4px 0',
        transition: '0.3s'
    }
});

// ─── Components ──────────────────────────────────────────────────
const HierarchyCard = ({ member, isExpanded, hasChildren, onToggle }) => {
    const { color, label, bg } = getRoleConfig(member.role);

    return (
        <Paper elevation={0} sx={cardStyles(color, isExpanded)} onClick={onToggle}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                    sx={{
                        width: 50,
                        height: 50,
                        bgcolor: bg,
                        color: color,
                        fontWeight: 800,
                        fontSize: '1.2rem',
                        border: `2px solid ${color}20`
                    }}
                >
                    {member?.name?.charAt(0) || '?'}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#1e293b', lineHeight: 1.2 }} noWrap>
                        {member?.name || 'N/A'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{ color: color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                        >
                            {label}
                        </Typography>
                    </Box>
                </Box>
                {hasChildren && (
                    <Box
                        sx={{
                            color: color,
                            transform: isExpanded ? 'rotate(90deg)' : 'none',
                            transition: '0.3s',
                            display: 'flex'
                        }}
                    >
                        <ChevronRightOutlined />
                    </Box>
                )}
            </Stack>
        </Paper>
    );
};

const HierarchyNode = ({ node, nodeLevel = 0, isLast = false, isExpanded, onToggleExpand }) => {
    const hasChildren = node.children && node.children.length > 0;
    const { color } = getRoleConfig(node.role);

    // Manage local accordion-style expansion for children
    const [expandedChildId, setExpandedChildId] = useState(null);

    const handleChildToggle = (childId) => {
        setExpandedChildId((prevId) => (prevId === childId ? null : childId));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                {/* Connector line from parent */}
                {nodeLevel > 0 && (
                    <>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: -40,
                                top: '50%',
                                width: 40,
                                height: '2px',
                                bgcolor: 'divider'
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                left: -40,
                                top: -40,
                                bottom: isLast ? '50%' : -40,
                                width: '2px',
                                bgcolor: 'divider'
                            }}
                        />
                    </>
                )}

                <HierarchyCard member={node} isExpanded={isExpanded} hasChildren={hasChildren} onToggle={onToggleExpand} />
            </Box>

            <AnimatePresence>
                {hasChildren && isExpanded && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: 'hidden', paddingLeft: 80, marginTop: 40, borderLeft: `2px dashed ${color}20` }}
                    >
                        <Stack spacing={4}>
                            {node.children.map((child, index) => (
                                <HierarchyNode
                                    key={child.id || index}
                                    node={child}
                                    nodeLevel={nodeLevel + 1}
                                    isLast={index === node.children.length - 1}
                                    isExpanded={expandedChildId === (child.id || index)}
                                    onToggleExpand={() => handleChildToggle(child.id || index)}
                                />
                            ))}
                        </Stack>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
};

// ─── Main View ────────────────────────────────────────────────────
const Hierarchy = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [hierarchyData, setHierarchyData] = useState([]);
    const [zoom, setZoom] = useState(1);

    // Accordion state for roots
    const [expandedRootId, setExpandedRootId] = useState(null);

    const fetchHierarchy = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getHierarchy();
            if (response?.data?.success) {
                setHierarchyData(response.data.data);
            }
        } catch (error) {
            console.error('Fetch hierarchy error:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHierarchy();
    }, [fetchHierarchy]);

    const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 1.5));
    const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));
    const handleResetZoom = () => setZoom(1);

    const handleRootToggle = (rootId) => {
        setExpandedRootId((prevId) => (prevId === rootId ? null : rootId));
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', pb: 10, display: 'flex', flexDirection: 'column' }}>
            {/* ── Page Header ── */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', // Vibrant Enterprise Blue
                        px: { xs: 2, md: 4 },
                        pt: 3,
                        pb: 12,
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '10px'
                    }}
                >
                    {/* Decorative circles */}
                    {[
                        { size: 200, top: -80, right: -60, op: 0.07 },
                        { size: 120, top: 10, right: 120, op: 0.05 }
                    ].map((c, i) => (
                        <Box
                            key={i}
                            sx={{
                                position: 'absolute',
                                top: c.top,
                                right: c.right,
                                width: c.size,
                                height: c.size,
                                borderRadius: '50%',
                                bgcolor: '#fff',
                                opacity: c.op,
                                pointerEvents: 'none'
                            }}
                        />
                    ))}

                    <IconButton
                        onClick={() => navigate('/hr/dashboard')}
                        sx={{ color: '#fff', mb: 1, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        size="small"
                    >
                        <ArrowBackIosNewOutlined fontSize="inherit" />
                    </IconButton>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h4" fontWeight={800} sx={{ color: '#fff' }}>
                                Employee Hierarchy
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <HomeOutlined sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    / HR Dashboard / Employee Hierarchy
                                </Typography>
                            </Box>
                        </Box>

                        <Stack direction="row" spacing={1.5}>
                            <Tooltip title="Refresh Structure">
                                <IconButton
                                    onClick={fetchHierarchy}
                                    sx={{
                                        color: '#fff',
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                                    }}
                                >
                                    <RefreshRounded />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                </Box>
            </motion.div>

            {/* Tree Workspace */}
            <Box sx={{ flexGrow: 1, mt: -8, position: 'relative', overflow: 'hidden' }}>
                <Container maxWidth="xl" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Paper
                        elevation={0}
                        sx={{
                            flexGrow: 1,
                            borderRadius: '30px',
                            bgcolor: '#fff',
                            border: '1px solid',
                            borderColor: 'divider',
                            position: 'relative',
                            overflow: 'hidden',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.06)'
                        }}
                    >
                        {/* Zoom Controls Overlay */}
                        <Stack
                            spacing={1}
                            sx={{
                                position: 'absolute',
                                bottom: 30,
                                right: 30,
                                zIndex: 10,
                                bgcolor: 'rgba(255,255,255,0.8)',
                                backdropFilter: 'blur(10px)',
                                p: 1,
                                borderRadius: '12px',
                                border: '1px solid rgba(0,0,0,0.05)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                            }}
                        >
                            <IconButton size="small" onClick={handleZoomIn}>
                                <ZoomInOutlined />
                            </IconButton>
                            <IconButton size="small" onClick={handleResetZoom}>
                                <RestartAltOutlined />
                            </IconButton>
                            <IconButton size="small" onClick={handleZoomOut}>
                                <ZoomOutOutlined />
                            </IconButton>
                            <Box sx={{ textAlign: 'center', py: 0.5 }}>
                                <Typography variant="caption" fontWeight={900}>
                                    {Math.round(zoom * 100)}%
                                </Typography>
                            </Box>
                        </Stack>

                        {/* Interactive Tree View */}
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                overflow: 'auto',
                                p: 6,
                                '&::-webkit-scrollbar': { width: 8, height: 8 },
                                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 10 }
                            }}
                        >
                            <AnimatePresence mode="wait">
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Stack spacing={2} alignItems="center">
                                            <CircularProgress size={60} thickness={4} sx={{ color: '#1e3a8a' }} />
                                            <Typography variant="body2" color="text.secondary" fontWeight={700}>
                                                Building reporting structure...
                                            </Typography>
                                        </Stack>
                                    </Box>
                                ) : hierarchyData.length > 0 ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: zoom }}
                                        transition={{ duration: 0.3 }}
                                        style={{ transformOrigin: 'top left', minWidth: 'max-content', paddingBottom: 100 }}
                                    >
                                        <Stack spacing={8}>
                                            {hierarchyData.map((root, index) => (
                                                <HierarchyNode
                                                    key={root.id || index}
                                                    node={root}
                                                    isExpanded={expandedRootId === (root.id || index)}
                                                    onToggleExpand={() => handleRootToggle(root.id || index)}
                                                />
                                            ))}
                                        </Stack>
                                    </motion.div>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Stack spacing={2} alignItems="center" sx={{ opacity: 0.5 }}>
                                            <AccountTreeOutlined sx={{ fontSize: 80 }} />
                                            <Typography variant="h5" fontWeight={800}>
                                                No Organization Data Found
                                            </Typography>
                                            <Typography variant="body2">Try refreshing the dashboard data</Typography>
                                        </Stack>
                                    </Box>
                                )}
                            </AnimatePresence>
                        </Box>
                    </Paper>
                </Container>
            </Box>
        </Box>
    );
};

export default Hierarchy;
